import React, { useEffect, useRef, useState } from "react";
import "./NextGenSchool.scss";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Helmet } from "react-helmet";
import { ArrowUpRight } from "iconoir-react";
import { Send, CheckCircle, Phone, Mail, MapPin, Download } from "lucide-react";
import {
  FaCheckCircle, FaUsers, FaGraduationCap, FaTrophy,
  FaRobot, FaMicrochip, FaLaptopCode,
  FaFlask, FaBolt, FaSchool, FaUserTie,
  FaHandshake, FaChartLine, FaBullseye, FaLightbulb,
  FaAward, FaShieldAlt, FaPlay,
} from "react-icons/fa";
import { GiCircuitry, GiRobotAntennas } from "react-icons/gi";
import { MdOutlinePrecisionManufacturing, MdSatelliteAlt } from "react-icons/md";
import emailjs from "@emailjs/browser";
import AOS from "aos";
import "aos/dist/aos.css";

import imgHero      from "../assets/nextgen/ng-hero.jpg";
import imgReelCover from "../assets/reel-cover-2.jpg";
import imgProblem   from "../assets/nextgen/ng-problem.jpg";
import imgLab1      from "../assets/nextgen/ng-lab-1.jpg";
import imgLab2      from "../assets/nextgen/ng-lab-2.jpg";
import imgLab3      from "../assets/nextgen/ng-lab-3.jpg";
import imgLab4      from "../assets/nextgen/ng-lab-4.jpeg";
import imgLab5      from "../assets/nextgen/ng-lab-5.jpg";
import imgLab6      from "../assets/nextgen/ng-lab-6.jpg";
import imgLab7      from "../assets/nextgen/ng-lab-7.jpg";
import imgLab8      from "../assets/nextgen/ng-lab-8.jpg";
import imgLab9      from "../assets/nextgen/ng-lab-9.jpg";
import imgLab10     from "../assets/nextgen/ng-lab-10.jpg";
import logoIhfc     from "../assets/incubation/logo-ihfc.svg";
import logoMnnit    from "../assets/incubation/logo-mnnit.png";
import logoKvs      from "../assets/partners/l-kvs.webp";
import logoNavodaya from "../assets/partners/l-jnv_11zon.webp";
import logoEmrs     from "../assets/incubation/logo-emrs.jpg";
import logoPmshri   from "../assets/incubation/logo-pmshri.png";
import logoSamagra  from "../assets/incubation/logo-samagra.png";
import logoDoSW     from "../assets/partners/l-DoSW.webp";

import mediaSharkTank      from "../assets/media/shark-tank-india.png";
import mediaYourStory      from "../assets/media/yourstory.svg";
import mediaHT             from "../assets/media/hindustan-times.svg";
import mediaTOI            from "../assets/media/times-of-india.svg";
import mediaIndiaToday     from "../assets/media/india-today.png";
import mediaBetterIndia    from "../assets/media/the-better-india.svg";
import mediaInc42          from "../assets/media/inc42.svg";
import mediaJoshTalks      from "../assets/media/josh-talks.png";
import mediaAmarUjala      from "../assets/media/amar-ujala.png";
import mediaDainikJagran   from "../assets/media/dainik-jagran.png";
import mediaMoneycontrol   from "../assets/media/moneycontrol.png";
import mediaEntrepreneur   from "../assets/media/entrepreneur-india.webp";
import mediaDailyhunt      from "../assets/media/dailyhunt.png";
import { impact } from "../data/stats";
import AsOfNote from "../components/AsOfNote";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

// ── Data ──────────────────────────────────────────────────────────────────────

const CHALLENGES = [
  "Labs remain unused — no trained mentors to run them",
  "No structured robotics or AI curriculum for students",
  "Teachers lack technical confidence to guide innovation",
  "Student engagement in STEM is consistently low",
  "Innovation activities are rare and unplanned",
  "Existing ATL infrastructure is severely underutilized",
];

const OFFERINGS = [
  {
    icon: <FaUserTie />,
    title: "Certified Technical Trainers",
    desc: "We deploy skilled, CoGrad-certified robotics and AI mentors directly to your school — so your lab has the expert it needs.",
    accent: "#0041e8",
  },
  {
    icon: <GiRobotAntennas />,
    title: "Structured Lab Curriculum",
    desc: "A session-wise, activity-driven curriculum covering Robotics, AI, IoT, Drone, Arduino, and Scratch — built for Indian schools.",
    accent: "#1d4ed8",
  },
  {
    icon: <FaFlask />,
    title: "Hands-On Project Learning",
    desc: "Students don't just watch — they build. Every session involves real projects: circuits, bots, IoT devices, and drones.",
    accent: "#7c3aed",
  },
  {
    icon: <FaShieldAlt />,
    title: "Complete ATL Lab Activation",
    desc: "From a dormant room to an active innovation ecosystem — we handle trainer deployment, activity planning, and progress tracking.",
    accent: "#0d9488",
  },
];

const STEPS = [
  {
    num: "01",
    icon: <FaBullseye />,
    title: "Assessment",
    desc: "We evaluate your school's existing infrastructure, student profile, and lab status to build a tailored program.",
  },
  {
    num: "02",
    icon: <FaHandshake />,
    title: "Onboarding",
    desc: "School and CoGrad sign an agreement. Trainer allocation, schedule, and curriculum are finalized together.",
  },
  {
    num: "03",
    icon: <FaLightbulb />,
    title: "Lab Activation",
    desc: "We breathe life into your ATL or Robotics lab — setting up equipment, organizing the space, and launching the first sessions.",
  },
  {
    num: "04",
    icon: <FaGraduationCap />,
    title: "Training & Innovation",
    desc: "Regular structured sessions by expert trainers — Robotics, AI, Drone, IoT, Coding, and real project work every week.",
  },
  {
    num: "05",
    icon: <FaChartLine />,
    title: "Growth & Tracking",
    desc: "Monthly reports, activity tracking, competition preparation, and continuous improvement of the innovation ecosystem.",
  },
];

const ACTIVITIES = [
  {
    icon: <GiCircuitry size={28} />,
    title: "Electronics & Circuits",
    desc: "Students build live circuit projects — hands-on experience with resistors, sensors, and real electronic components.",
    tag: "ATL Lab",
    color: "#0041e8",
  },
  {
    icon: <MdOutlinePrecisionManufacturing size={28} />,
    title: "Drone Assembly",
    desc: "Students assemble, calibrate, and fly drone components with precision — understanding aerodynamics and electronics.",
    tag: "Advanced",
    color: "#1d4ed8",
  },
  {
    icon: <FaMicrochip size={28} />,
    title: "Arduino & Robotics",
    desc: "Programming Arduino microcontrollers, building bots, and solving real engineering problems through iterative prototyping.",
    tag: "Robotics",
    color: "#7c3aed",
  },
  {
    icon: <MdSatelliteAlt size={28} />,
    title: "IoT Based Projects",
    desc: "Students design and build Internet of Things devices — smart sensors, automation systems, and data-connected gadgets.",
    tag: "IoT",
    color: "#0d9488",
  },
  {
    icon: <FaLaptopCode size={28} />,
    title: "Scratch Coding",
    desc: "Block-based visual programming on interactive displays — the gateway to computational thinking for young learners.",
    tag: "Coding",
    color: "#2563eb",
  },
  {
    icon: <FaRobot size={28} />,
    title: "AI & STEM Projects",
    desc: "Guided AI workshops, STEM exploration sessions, and innovation challenges that build future-ready skills from school.",
    tag: "AI / STEM",
    color: "#4f46e5",
  },
];

const SCHOOLS_GET = [
  {
    icon: <FaRobot />,
    title: "ATL & Robotics Implementation",
    items: ["ATL & Robotics lab activation", "Robotics, AI & IoT live sessions", "Practical hands-on project learning"],
  },
  {
    icon: <FaSchool />,
    title: "School Systems & Execution",
    items: ["Regular workshop scheduling", "Performance & activity tracking", "Complete lab utilization support"],
  },
  {
    icon: <FaUsers />,
    title: "Teacher & Student Development",
    items: ["Teacher training & mentoring", "Student innovation exposure", "Competition preparation & coaching"],
  },
  {
    icon: <FaLightbulb />,
    title: "Growth & Innovation Culture",
    items: ["Innovation ecosystem building", "Stronger school positioning", "Real-world technology exposure"],
  },
];

const AWARDS = [
  {
    icon: <FaTrophy />,
    title: "Top Social Impact Entrepreneur India",
    body: "Awarded by the Former Chief Justice of India for outstanding contribution to education and social impact.",
    tag: "National Award",
    color: "#0041e8",
  },
  {
    icon: <FaAward />,
    title: "Best Emerging Rural Skilling Platform",
    body: "Recognized by Entrepreneur India Excellence Awards 2024 — Social Entrepreneur of the Year for Founders Saurabh Yadav & Himanshu Chaurasiya.",
    tag: "Entrepreneur India",
    color: "#0041e8",
  },
  {
    icon: <FaGraduationCap />,
    title: "Featured on Shark Tank India",
    body: "CoGrad's mission to transform rural school education was recognized on India's premier startup investment platform.",
    tag: "National Platform",
    color: "#7c3aed",
  },
];

const PARTNERS = [
  { logo: logoNavodaya, name: "Navodaya Vidyalaya Samiti", tag: "120+ Schools" },
  { logo: logoKvs,      name: "Kendriya Vidyalaya Sangathan", tag: "Central Schools" },
  { logo: logoEmrs,     name: "EMRS — Tribal Schools", tag: "Ministry of Tribal Affairs" },
  { logo: logoPmshri,   name: "PM SHRI Schools", tag: "Flagship Scheme" },
  { logo: logoIhfc,     name: "IHFC — IIT Delhi", tag: "Research Institute" },
  { logo: logoMnnit,    name: "MNNIT Allahabad", tag: "MoU Partner" },
  { logo: logoSamagra,  name: "Samagra Shiksha", tag: "Govt. Initiative" },
  { logo: logoDoSW,     name: "Dept. of Social Welfare, UP", tag: "State Government" },
];

const MARQUEE_ITEMS = [
  "NextGen School Innovation Program",
  "ATL Lab Activation",
  "Robotics & AI Training",
  "120+ Navodaya Schools",
  "10,000+ Students Trained",
  "Expert Certified Trainers",
  "NEP 2020 Aligned",
  "Drone & IoT Projects",
];

const MEDIA_LOGOS = [
  { name: "Shark Tank India",   src: mediaSharkTank },
  { name: "YourStory",          src: mediaYourStory },
  { name: "Hindustan Times",    src: mediaHT },
  { name: "Times of India",     src: mediaTOI },
  { name: "India Today",        src: mediaIndiaToday },
  { name: "The Better India",   src: mediaBetterIndia },
  { name: "Inc42",              src: mediaInc42 },
  { name: "Josh Talks",         src: mediaJoshTalks },
  { name: "Amar Ujala",         src: mediaAmarUjala },
  { name: "Dainik Jagran",      src: mediaDainikJagran },
  { name: "Moneycontrol",       src: mediaMoneycontrol },
  { name: "Entrepreneur India", src: mediaEntrepreneur },
  { name: "Dailyhunt",          src: mediaDailyhunt },
];

const EMPTY_FORM = {
  schoolName: "", contactName: "", email: "",
  phone: "", city: "", schoolType: "",
  studentCount: "", hasAtlLab: "", message: "",
};

// ── Component ─────────────────────────────────────────────────────────────────

const NextGenSchool = () => {
  const formRef = useRef(null);

  const [form,          setForm]          = useState(EMPTY_FORM);
  const [errors,        setErrors]        = useState({});
  const [isLoading,     setIsLoading]     = useState(false);
  const [sent,          setSent]          = useState(false);
  const [submitError,   setSubmitError]   = useState("");
  const [lightbox,      setLightbox]      = useState(null);
  const [videoOpen,     setVideoOpen]     = useState(false);
  const [phoneHovered,  setPhoneHovered]  = useState(false);

  useEffect(() => {
    AOS.init({ duration: 500, once: true, offset: 30, easing: 'ease-out' });
    const t = setTimeout(() => AOS.refreshHard(), 100);
    window.scrollTo(0, 0);
    emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);
    return () => clearTimeout(t);
  }, []);

  const GALLERY_ITEMS = [
    { img: imgLab1,  label: "Robotics & ATL Lab Sessions", main: true },
    { img: imgLab2,  label: "Drone Assembly" },
    { img: imgLab3,  label: "Arduino & Circuits" },
    { img: imgLab4,  label: "AI & Coding" },
    { img: imgLab5,  label: "IoT Projects" },
    { img: imgLab6,  label: "Student Innovation" },
    { img: imgLab7,  label: "STEM Workshop" },
    { img: imgLab8,  label: "Hands-On Learning" },
    { img: imgLab9,  label: "Tech Exploration" },
    { img: imgLab10, label: "Innovation Challenge" },
  ];

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((i) => (i + 1) % GALLERY_ITEMS.length);
      if (e.key === "ArrowLeft")  setLightbox((i) => (i - 1 + GALLERY_ITEMS.length) % GALLERY_ITEMS.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, GALLERY_ITEMS.length]);


  const scrollToForm = () =>
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.schoolName.trim())   e.schoolName   = "School name is required";
    if (!form.contactName.trim())  e.contactName  = "Contact name is required";
    if (!form.email.trim())        e.email        = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Enter a valid email address";
    if (!form.phone.trim())        e.phone        = "Phone number is required";
    if (!form.schoolType)          e.schoolType   = "Please select school type";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    if (!validate()) return;
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/nextgen`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Server error");

      emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          to_name: "Admin",
          message: `NextGen School Registration\n\nSchool: ${form.schoolName}\nContact: ${form.contactName}\nEmail: ${form.email}\nPhone: ${form.phone}\nCity: ${form.city || "—"}\nType: ${form.schoolType}\nStudents: ${form.studentCount || "—"}\nATL Lab: ${form.hasAtlLab || "—"}\nMessage: ${form.message || "—"}`,
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      ).catch(() => {});

      setSent(true);
      setForm(EMPTY_FORM);
    } catch {
      setSubmitError("Failed to submit. Please email us at connect@cograd.in");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="ngs">
      <Helmet>
        <title>NextGen School Innovation Program | CoGrad</title>
        <meta
          name="description"
          content="CoGrad's NextGen School Innovation Program — activating ATL Labs with expert trainers, robotics, AI & IoT sessions across 120+ Navodaya schools and 10,000+ students trained."
        />
      </Helmet>

      <Header />

      {/* ══ HERO ══════════════════════════════════════════════════════════════ */}
      <section className="ngs-hero">
        <div className="ngs-hero__bg">
          <div className="ngs-hero__grid" />
          <div className="ngs-hero__glow ngs-hero__glow--a" />
          <div className="ngs-hero__glow ngs-hero__glow--b" />
          <div className="ngs-hero__glow ngs-hero__glow--c" />
        </div>

        <div className="ngs-wrap ngs-hero__inner">
          {/* Left — copy */}
          <div className="ngs-hero__copy" data-aos="fade-up">
            <div className="ngs-hero__badge">
              <span className="ngs-hero__badge-dot" />
              2026 Program — Enrolling Schools Now
            </div>

            <h1 className="ngs-hero__h1">
              Turning School Labs<br />
              Into <span className="ngs-accent">Innovation</span><br />
              Centers
            </h1>

            <div className="ngs-hero__tech-pills">
              <span>Robotics</span>
              <span>AI</span>
              <span>IoT</span>
              <span>Drone</span>
              <span>Arduino</span>
            </div>

            <p className="ngs-hero__sub">
              CoGrad's NextGen School Innovation Program deploys certified trainers,
              structured curriculum, and hands-on Robotics, AI &amp; IoT sessions —
              activating your school's ATL lab from day one.
            </p>

            <div className="ngs-hero__cta">
              <button className="ngs-btn ngs-btn--primary" onClick={scrollToForm}>
                Partner With Us <ArrowUpRight width={18} height={18} />
              </button>
              <a href="#program" className="ngs-btn ngs-btn--ghost">
                View Program
              </a>
            </div>
          </div>

          {/* Right — visual */}
          <div className="ngs-hero__visual" data-aos="fade-left" data-aos-delay="140">
            <div className="ngs-hero__img-wrap">
              <div className="ngs-hero__img-inner">
                <img src={imgHero} alt="NextGen School Innovation Program — Students in ATL Lab" />
                <div className="ngs-hero__img-veil" />
              </div>
              <div className="ngs-hero__float-badge ngs-hero__float-badge--tl">
                <FaRobot />
                <span>Robotics & Drone</span>
              </div>
              <div className="ngs-hero__float-badge ngs-hero__float-badge--tr">
                <FaMicrochip />
                <span>AI & IoT</span>
              </div>
              <div className="ngs-hero__float-badge ngs-hero__float-badge--br">
                <FaUsers />
                <span>10,000+ Students</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ MARQUEE ══════════════════════════════════════════════════════════ */}
      <div className="ngs-marquee" aria-hidden="true">
        <div className="ngs-marquee__track">
          {[0, 1].map((k) => (
            <div key={k} className="ngs-marquee__row">
              {MARQUEE_ITEMS.map((item, i) => (
                <React.Fragment key={i}>
                  <span>{item}</span>
                  <span className="ngs-marquee__sep">✦</span>
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ══ FEATURED VIDEO / IMPACT ══════════════════════════════════════════ */}
      <section className="ngs-video">
        <div className="ngs-wrap">
          <div className="ngs-video__inner">

            {/* Left — phone mockup */}
            <div className="ngs-video__phone-col" data-aos="fade-right">
              <div
                className={`ngs-video__phone${phoneHovered ? " ngs-video__phone--active" : ""}`}
                onMouseEnter={() => setPhoneHovered(true)}
                onMouseLeave={() => setPhoneHovered(false)}
                onClick={() => setVideoOpen(true)}
                role="button"
                tabIndex={0}
                aria-label="Watch Neetu Singh testimonial video"
              >
                <div className="ngs-video__phone-notch" />
                <img
                  src={imgReelCover}
                  alt="Neetu Singh testimonial"
                  className="ngs-video__phone-thumb"
                />
                {phoneHovered ? (
                  <iframe
                    className="ngs-video__phone-iframe"
                    src="https://www.youtube.com/embed/laTXbTEG68g?autoplay=1&mute=1&rel=0&controls=0"
                    title="Preview"
                    allow="autoplay"
                  />
                ) : (
                  <div className="ngs-video__phone-play">
                    <FaPlay />
                  </div>
                )}
              </div>
              <div className="ngs-video__phone-meta">
                <strong>Neetu Singh</strong>
                <span>Teacher · Founder · KD Live (Ed-Tech Platform)</span>
              </div>
            </div>

            {/* Right — copy */}
            <div className="ngs-video__copy" data-aos="fade-left" data-aos-delay="100">
              <span className="ngs-tag">Educator Voices</span>
              <h2 className="ngs-video__h2">
                Voices from educators who are{" "}
                <span className="ngs-accent">reshaping India's classrooms</span>
              </h2>
              <p className="ngs-video__sub">
                Real stories from school leaders and educators who have
                experienced CoGrad's transformation firsthand.
              </p>
              <div className="ngs-video__stats">
                <div className="ngs-video__stat">
                  <strong>26+</strong>
                  <span>Schools Incubated</span>
                </div>
                <div className="ngs-video__stat">
                  <strong>{impact.students.short}</strong>
                  <span>Students Impacted</span>
                </div>
                <div className="ngs-video__stat">
                  <strong>5K+</strong>
                  <span>Workshops Conducted</span>
                </div>
              </div>
              <p className="ngs-video__hint">Hover on the phone to watch their story</p>
            </div>

          </div>
        </div>

        {/* Video modal */}
        {videoOpen && (
          <div className="ngs-video__modal" onClick={() => setVideoOpen(false)}>
            <button
              className="ngs-video__modal-close"
              onClick={() => setVideoOpen(false)}
              aria-label="Close video"
            >✕</button>
            <div className="ngs-video__modal-inner" onClick={(e) => e.stopPropagation()}>
              <iframe
                src="https://www.youtube.com/embed/laTXbTEG68g?autoplay=1&rel=0"
                title="CoGrad NextGen School — Neetu Singh"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        )}
      </section>

      {/* ══ PROBLEM ══════════════════════════════════════════════════════════ */}
      <section className="ngs-problem">
        <div className="ngs-wrap ngs-problem__grid">

          <div className="ngs-problem__copy" data-aos="fade-right">
            <span className="ngs-tag">The Real Problem</span>
            <h2 className="ngs-h2">
              Most schools have labs.<br />
              Almost none have <span className="ngs-accent">mentors</span>.
            </h2>
            <p className="ngs-problem__body">
              Across India, thousands of ATL and Robotics Labs sit unused — funded
              by the government, built by schools, but never truly activated. The
              missing piece isn't infrastructure. It's expertise.
            </p>
            <ul className="ngs-challenges">
              {CHALLENGES.map((c, i) => (
                <li key={i} data-aos="fade-up" data-aos-delay={i * 60}>
                  <span className="ngs-challenges__dot" />
                  {c}
                </li>
              ))}
            </ul>
            <div className="ngs-problem__insight">
              <FaBolt />
              <span>"Labs Create Opportunities — Mentors Create Innovation"</span>
            </div>
          </div>

          <div className="ngs-problem__visual" data-aos="fade-left" data-aos-delay="100">
            <div className="ngs-problem__img-wrap">
              <img src={imgProblem} alt="CoGrad trainer with students in school" />
              <div className="ngs-problem__img-veil" />
            </div>
            <div className="ngs-problem__stat-cards">
              <div className="ngs-problem__stat-card">
                <strong>120+</strong>
                <span>Navodaya Schools Served</span>
              </div>
              <div className="ngs-problem__stat-card ngs-problem__stat-card--accent">
                <strong>10,000+</strong>
                <span>Students Trained</span>
              </div>
              <div className="ngs-problem__stat-card">
                <strong>50+</strong>
                <span>Innovation Workshops</span>
              </div>
            </div>
            <AsOfNote className="asof--center" />
          </div>

        </div>
      </section>

      {/* ══ OFFERINGS ════════════════════════════════════════════════════════ */}
      <section className="ngs-offerings" id="program">
        <div className="ngs-wrap">
          <div className="ngs-section-head" data-aos="fade-up">
            <span className="ngs-tag">What We Provide</span>
            <h2 className="ngs-h2">Everything a school needs to innovate</h2>
            <p className="ngs-lead">
              We don't just set up labs — we build active, ongoing innovation
              ecosystems inside your school with expert people and a proven system.
            </p>
          </div>

          <div className="ngs-offerings__grid">
            {OFFERINGS.map((o, i) => (
              <div
                className="ngs-offer-card"
                key={o.title}
                data-aos="fade-up"
                data-aos-delay={i * 80}
                style={{ "--accent": o.accent }}
              >
                <div className="ngs-offer-card__icon">{o.icon}</div>
                <h3 className="ngs-offer-card__title">{o.title}</h3>
                <p className="ngs-offer-card__desc">{o.desc}</p>
                <div className="ngs-offer-card__bar" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ PROCESS ══════════════════════════════════════════════════════════ */}
      <section className="ngs-process">
        <div className="ngs-wrap">
          <div className="ngs-section-head" data-aos="fade-up">
            <span className="ngs-tag">How It Works</span>
            <h2 className="ngs-h2">From empty lab to innovation center</h2>
            <p className="ngs-lead">
              A structured 5-step journey — from initial assessment to a thriving
              innovation ecosystem inside your school.
            </p>
          </div>

          <div className="ngs-process__steps">
            {STEPS.map((s, i) => (
              <div
                className="ngs-process__step"
                key={s.title}
                data-aos="fade-up"
                data-aos-delay={i * 90}
              >
                <div className="ngs-process__step-head">
                  <div className="ngs-process__step-dot">
                    <span>{s.num}</span>
                  </div>
                  {i < STEPS.length - 1 && <div className="ngs-process__step-line" />}
                </div>
                <div className="ngs-process__step-icon">{s.icon}</div>
                <h3 className="ngs-process__step-title">{s.title}</h3>
                <p className="ngs-process__step-desc">{s.desc}</p>
              </div>
            ))}
          </div>

          <div className="ngs-process__cta" data-aos="fade-up">
            <button className="ngs-btn ngs-btn--primary" onClick={scrollToForm}>
              Register Your School <ArrowUpRight width={18} height={18} />
            </button>
          </div>
        </div>
      </section>

      {/* ══ ACTIVITIES ═══════════════════════════════════════════════════════ */}
      <section className="ngs-activities">
        <div className="ngs-wrap">
          <div className="ngs-section-head" data-aos="fade-up">
            <span className="ngs-tag">Student Activities</span>
            <h2 className="ngs-h2">What students actually build</h2>
            <p className="ngs-lead">
              Real hands-on sessions — not just theory. Every CoGrad visit ends
              with a project students can hold, demonstrate, and be proud of.
            </p>
          </div>

          <div className="ngs-activities__grid">
            {ACTIVITIES.map((a, i) => (
              <div
                className="ngs-act-card"
                key={a.title}
                data-aos="fade-up"
                data-aos-delay={i * 70}
                style={{ "--accent": a.color }}
              >
                <div className="ngs-act-card__icon">{a.icon}</div>
                <span className="ngs-act-card__tag">{a.tag}</span>
                <h3 className="ngs-act-card__title">{a.title}</h3>
                <p className="ngs-act-card__desc">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ IN ACTION PHOTO GRID ═════════════════════════════════════════════ */}
      <section className="ngs-gallery">
        <div className="ngs-wrap">
          <div className="ngs-section-head" data-aos="fade-up">
            <span className="ngs-tag">Real Program</span>
            <h2 className="ngs-h2">Students building, learning, innovating</h2>
            <p className="ngs-lead">
              Every session ends with something students can hold, fly, program, or demonstrate —
              real innovation happening inside India's schools.
            </p>
          </div>

          <div className="ngs-gallery__bento" data-aos="fade-up">
            {GALLERY_ITEMS.map((item, i) => (
              <div
                key={i}
                className={`ngs-gallery__bento-cell${item.main ? " ngs-gallery__bento-cell--main" : ""}`}
                onClick={() => setLightbox(i)}
              >
                <img src={item.img} alt={item.label} />
                <div className="ngs-gallery__bento-foot">
                  <span className="ngs-gallery__bento-tag">{item.label}</span>
                </div>
                <div className="ngs-gallery__bento-zoom" aria-hidden="true">⤢</div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* ══ WHAT SCHOOLS GET ═════════════════════════════════════════════════ */}
      <section className="ngs-get">
        <div className="ngs-wrap">
          <div className="ngs-section-head" data-aos="fade-up">
            <span className="ngs-tag">Complete Package</span>
            <h2 className="ngs-h2">What your school gets</h2>
            <p className="ngs-lead">
              A complete ATL &amp; Robotics support system — not just equipment,
              but a fully managed innovation program from day one.
            </p>
          </div>

          <div className="ngs-get__grid">
            {SCHOOLS_GET.map((g, i) => (
              <div
                className="ngs-get-card"
                key={g.title}
                data-aos="fade-up"
                data-aos-delay={i * 80}
              >
                <div className="ngs-get-card__icon">{g.icon}</div>
                <h3 className="ngs-get-card__title">{g.title}</h3>
                <ul className="ngs-get-card__list">
                  {g.items.map((item) => (
                    <li key={item}>
                      <FaCheckCircle />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="ngs-get__footer" data-aos="fade-up">
            <span>Hands-on learning</span>
            <span className="ngs-get__footer-sep">·</span>
            <span>Structured execution</span>
            <span className="ngs-get__footer-sep">·</span>
            <span className="ngs-get__footer-accent">Real innovation</span>
          </div>
        </div>
      </section>

      {/* ══ AWARDS ═══════════════════════════════════════════════════════════ */}
      <section className="ngs-awards">
        <div className="ngs-wrap">
          <div className="ngs-section-head" data-aos="fade-up">
            <span className="ngs-tag">Recognition</span>
            <h2 className="ngs-h2">Trusted & award-winning</h2>
            <p className="ngs-lead">
              CoGrad's impact in rural school education has been recognized at the
              highest national levels — from Shark Tank India to the Supreme Court.
            </p>
          </div>

          <div className="ngs-awards__grid">
            {AWARDS.map((a, i) => (
              <div
                className="ngs-award-card"
                key={a.title}
                data-aos="fade-up"
                data-aos-delay={i * 90}
                style={{ "--accent": a.color }}
              >
                <div className="ngs-award-card__icon">{a.icon}</div>
                <span className="ngs-award-card__tag">{a.tag}</span>
                <h3 className="ngs-award-card__title">{a.title}</h3>
                <p className="ngs-award-card__body">{a.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ PARTNERS ═════════════════════════════════════════════════════════ */}
      <section className="ngs-partners">
        <div className="ngs-wrap">
          <div className="ngs-section-head" data-aos="fade-up">
            <span className="ngs-tag">Institutional Partners</span>
            <h2 className="ngs-h2">Trusted by India's top education bodies</h2>
            <p className="ngs-lead">
              CoGrad operates in partnership with the country's most credible
              government schools, institutes, and education agencies.
            </p>
          </div>

          {/* Desktop grid */}
          <div className="ngs-partners__grid">
            {PARTNERS.map((p, i) => (
              <div
                className="ngs-partner-card"
                key={i}
                data-aos="fade-up"
                data-aos-delay={(i % 4) * 60}
              >
                <div className="ngs-partner-card__logo-box">
                  <img src={p.logo} alt={p.name} />
                </div>
                <div className="ngs-partner-card__info">
                  <span className="ngs-partner-card__name">{p.name}</span>
                  <span className="ngs-partner-card__badge">{p.tag}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile ticker */}
          <div className="ngs-partners__viewport">
            <div className="ngs-partners__track">
              {[...PARTNERS, ...PARTNERS].map((p, i) => (
                <div className="ngs-partner-card" key={i}>
                  <div className="ngs-partner-card__logo-box">
                    <img src={p.logo} alt={p.name} />
                  </div>
                  <div className="ngs-partner-card__info">
                    <span className="ngs-partner-card__name">{p.name}</span>
                    <span className="ngs-partner-card__badge">{p.tag}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ MEDIA ════════════════════════════════════════════════════════════ */}
      <section className="ngs-media">
        <div className="ngs-wrap">
          <div className="ngs-section-head" data-aos="fade-up">
            <span className="ngs-tag">Media Coverage</span>
            <h2 className="ngs-h2">Covered by India's leading media</h2>
          </div>
          <div className="ngs-media__viewport" aria-hidden="true">
            <div className="ngs-media__track">
              {[...MEDIA_LOGOS, ...MEDIA_LOGOS].map((m, i) => (
                <div key={i} className="ngs-media__item">
                  <img src={m.src} alt={m.name} className="ngs-media__logo" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ BROCHURE ═════════════════════════════════════════════════════════ */}
      <section className="ngs-brochure">
        <div className="ngs-wrap">
          <div className="ngs-brochure__card" data-aos="fade-up">
            <div className="ngs-brochure__bg-grid" />
            <div className="ngs-brochure__glow" />

            <div className="ngs-brochure__content">
              {/* Left — copy */}
              <div className="ngs-brochure__copy">
                <span className="ngs-tag">Free Download</span>
                <h2 className="ngs-brochure__h2">
                  Get the Full Program<br />
                  <span className="ngs-brochure__hl">Brochure</span>
                </h2>
                <p className="ngs-brochure__sub">
                  Everything your school needs to know about the NextGen Innovation
                  Program — curriculum, trainer profiles, partner schools, and more.
                </p>
                <ul className="ngs-brochure__list">
                  {[
                    "Complete session-wise curriculum overview",
                    "ATL & Robotics lab activation guide",
                    "Student project gallery & outcomes",
                    "Trainer certification & deployment model",
                    "Partner school testimonials",
                    "Pricing & partnership models",
                  ].map((item) => (
                    <li key={item}>
                      <FaCheckCircle />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="ngs-brochure__actions">
                  <a
                    href="/brochure-nextgen-school.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ngs-btn ngs-btn--primary"
                  >
                    <Download size={17} />
                    Download Brochure
                  </a>
                  <button className="ngs-btn ngs-btn--ghost" onClick={scrollToForm}>
                    Register School <ArrowUpRight width={18} height={18} />
                  </button>
                </div>
              </div>

              {/* Right — Canva brochure embed */}
              <div className="ngs-brochure__visual" data-aos="fade-left" data-aos-delay="120">
                <div className="ngs-brochure__embed-card">
                  <div className="ngs-brochure__embed-topbar">
                    <div className="ngs-brochure__embed-dots">
                      <span /><span /><span />
                    </div>
                    <span className="ngs-brochure__embed-filename">NextGen School Brochure · 2026</span>
                  </div>
                  <div className="ngs-brochure__embed-frame">
                    <iframe
                      loading="lazy"
                      src="https://www.canva.com/design/DAHKXl3uBG0/E5lyiEDLrThvnOyoTLwinw/view?embed"
                      allowFullScreen
                      allow="fullscreen"
                      title="NextGen School Innovation Program Brochure"
                    />
                  </div>
                  <a
                    href="https://www.canva.com/design/DAHKXl3uBG0/E5lyiEDLrThvnOyoTLwinw/view"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ngs-brochure__embed-open"
                  >
                    <Download size={15} /> Open Full Brochure
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ REGISTRATION FORM ════════════════════════════════════════════════ */}
      <section className="ngs-register" ref={formRef}>
        <div className="ngs-wrap ngs-register__grid">

          {/* Left — info */}
          <div className="ngs-register__info" data-aos="fade-right">
            <span className="ngs-tag">Partner With Us</span>
            <h2 className="ngs-h2 ngs-h2--left">
              Register your <span className="ngs-accent">school</span> today
            </h2>
            <p className="ngs-register__sub">
              Fill in the details and our team will reach out within 24 hours
              to discuss the NextGen School Innovation Program for your institution.
            </p>

            <ul className="ngs-checklist">
              <li><FaCheckCircle /><span>Certified trainer deployed to your school</span></li>
              <li><FaCheckCircle /><span>Structured robotics, AI &amp; IoT curriculum</span></li>
              <li><FaCheckCircle /><span>ATL lab activation &amp; full setup support</span></li>
              <li><FaCheckCircle /><span>Regular workshops &amp; student projects</span></li>
              <li><FaCheckCircle /><span>Monthly progress reports &amp; tracking</span></li>
              <li><FaCheckCircle /><span>Competition preparation &amp; mentoring</span></li>
            </ul>

            <div className="ngs-register__contact-cards">
              <a href="tel:+919220253001" className="ngs-register__contact-card">
                <div className="ngs-register__contact-icon"><Phone size={17} /></div>
                <div>
                  <span>Call Us</span>
                  <strong>+91-9220253001</strong>
                </div>
              </a>
              <a href="mailto:connect@cograd.in" className="ngs-register__contact-card">
                <div className="ngs-register__contact-icon"><Mail size={17} /></div>
                <div>
                  <span>Email Us</span>
                  <strong>connect@cograd.in</strong>
                </div>
              </a>
              <div className="ngs-register__contact-card ngs-register__contact-card--plain">
                <div className="ngs-register__contact-icon"><MapPin size={17} /></div>
                <div>
                  <span>Office</span>
                  <strong>Sector 62, Noida</strong>
                </div>
              </div>
            </div>
          </div>

          {/* Right — form */}
          <div className="ngs-register__card" data-aos="fade-left" data-aos-delay="80">
            {sent ? (
              <div className="ngs-register__success">
                <div className="ngs-register__success-icon">
                  <CheckCircle size={44} strokeWidth={1.6} />
                </div>
                <h3>Registration Received!</h3>
                <p>
                  Thank you! Our team will contact you within 24 hours to
                  discuss the NextGen School Innovation Program for your school.
                </p>
                <button className="ngs-btn ngs-btn--ghost" onClick={() => setSent(false)}>
                  Submit Another Registration
                </button>
              </div>
            ) : (
              <>
                <div className="ngs-register__card-head">
                  <h3>School Registration</h3>
                  <p>Tell us about your school and innovation goals.</p>
                </div>

                <form className="ngs-form" onSubmit={handleSubmit} noValidate>

                  <div className="ngs-form__row ngs-form__row--2">
                    <div className={`ngs-field${errors.schoolName ? " ngs-field--err" : ""}`}>
                      <label>School Name *</label>
                      <input
                        type="text" name="schoolName"
                        placeholder="e.g. JNV Hamirpur, UP"
                        value={form.schoolName} onChange={handleChange}
                        disabled={isLoading}
                      />
                      {errors.schoolName && <span>{errors.schoolName}</span>}
                    </div>
                    <div className={`ngs-field${errors.contactName ? " ngs-field--err" : ""}`}>
                      <label>Contact Person *</label>
                      <input
                        type="text" name="contactName"
                        placeholder="e.g. Rajesh Kumar (Principal)"
                        value={form.contactName} onChange={handleChange}
                        disabled={isLoading}
                      />
                      {errors.contactName && <span>{errors.contactName}</span>}
                    </div>
                  </div>

                  <div className="ngs-form__row ngs-form__row--2">
                    <div className={`ngs-field${errors.email ? " ngs-field--err" : ""}`}>
                      <label>Email Address *</label>
                      <input
                        type="email" name="email"
                        placeholder="principal@school.edu.in"
                        value={form.email} onChange={handleChange}
                        disabled={isLoading}
                      />
                      {errors.email && <span>{errors.email}</span>}
                    </div>
                    <div className={`ngs-field${errors.phone ? " ngs-field--err" : ""}`}>
                      <label>Phone Number *</label>
                      <input
                        type="tel" name="phone"
                        placeholder="+91 98765 43210"
                        value={form.phone} onChange={handleChange}
                        disabled={isLoading}
                      />
                      {errors.phone && <span>{errors.phone}</span>}
                    </div>
                  </div>

                  <div className="ngs-form__row ngs-form__row--2">
                    <div className="ngs-field">
                      <label>City &amp; State</label>
                      <input
                        type="text" name="city"
                        placeholder="e.g. Hamirpur, UP"
                        value={form.city} onChange={handleChange}
                        disabled={isLoading}
                      />
                    </div>
                    <div className={`ngs-field${errors.schoolType ? " ngs-field--err" : ""}`}>
                      <label>School Type *</label>
                      <select name="schoolType" value={form.schoolType} onChange={handleChange} disabled={isLoading}>
                        <option value="">Select type</option>
                        <option value="NVS">Navodaya Vidyalaya (NVS)</option>
                        <option value="KVS">Kendriya Vidyalaya (KVS)</option>
                        <option value="EMRS">Eklavya Model School (EMRS)</option>
                        <option value="PM SHRI">PM SHRI School</option>
                        <option value="State Government">State Government School</option>
                        <option value="Private CBSE">Private School (CBSE)</option>
                        <option value="Other">Other</option>
                      </select>
                      {errors.schoolType && <span>{errors.schoolType}</span>}
                    </div>
                  </div>

                  <div className="ngs-form__row ngs-form__row--2">
                    <div className="ngs-field">
                      <label>No. of Students</label>
                      <input
                        type="number" name="studentCount" min="1"
                        placeholder="e.g. 500"
                        value={form.studentCount} onChange={handleChange}
                        disabled={isLoading}
                      />
                    </div>
                    <div className="ngs-field">
                      <label>Do you have an ATL Lab?</label>
                      <select name="hasAtlLab" value={form.hasAtlLab} onChange={handleChange} disabled={isLoading}>
                        <option value="">Select</option>
                        <option value="Yes — Active">Yes, it's active</option>
                        <option value="Yes — Inactive">Yes, but it's inactive</option>
                        <option value="Robotics Lab">We have a Robotics Lab</option>
                        <option value="No">No lab yet</option>
                      </select>
                    </div>
                  </div>

                  <div className="ngs-field">
                    <label>Message / Requirements</label>
                    <textarea
                      name="message" rows={3}
                      placeholder="Tell us about your innovation goals, preferred timing, or any specific requirements…"
                      value={form.message} onChange={handleChange}
                      disabled={isLoading}
                    />
                  </div>

                  {submitError && (
                    <div className="ngs-form__submit-err">{submitError}</div>
                  )}

                  <button
                    type="submit"
                    className={`ngs-btn ngs-btn--primary ngs-form__submit${isLoading ? " ngs-form__submit--loading" : ""}`}
                    disabled={isLoading}
                  >
                    {!isLoading && <Send size={16} />}
                    {isLoading ? "Submitting…" : "Submit Registration"}
                  </button>

                  <p className="ngs-form__note">
                    We typically respond within 24 business hours.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ══ LIGHTBOX ═════════════════════════════════════════════════════════ */}
      {lightbox !== null && (
        <div className="ngs-lightbox" onClick={() => setLightbox(null)}>
          <button className="ngs-lightbox__close" onClick={() => setLightbox(null)} aria-label="Close">✕</button>
          <button
            className="ngs-lightbox__nav ngs-lightbox__nav--prev"
            onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i - 1 + GALLERY_ITEMS.length) % GALLERY_ITEMS.length); }}
            aria-label="Previous"
          >‹</button>
          <div className="ngs-lightbox__img-wrap" onClick={(e) => e.stopPropagation()}>
            <img src={GALLERY_ITEMS[lightbox].img} alt={GALLERY_ITEMS[lightbox].label} />
            <span className="ngs-lightbox__caption">{GALLERY_ITEMS[lightbox].label}</span>
          </div>
          <button
            className="ngs-lightbox__nav ngs-lightbox__nav--next"
            onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i + 1) % GALLERY_ITEMS.length); }}
            aria-label="Next"
          >›</button>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default NextGenSchool;
